import { useCallback, useState } from 'react';
import axios from 'axios';

const TEMPORARILY_UNAVAILABLE = 'Temporarily unavailable. Please try again later.';

/**
 * A generic hook to post form data to an URL.
 *
 * @param {string} url The URL to post the data to.
 * @param {Function} handleSuccess The callback when the call succeeds.
 * @param {boolean|Function} handleError If a function and the call fails, then this is called
 *                                     with the request error, so the caller can branch on the status.
 *
 * @returns {Array} An array containing the onSubmit handler, the loading and error state.
 */
export default (url, handleSuccess, handleError = false) => {
  // Define initial state.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create submit handler.
  const onSubmit = useCallback(
    async (formData) => {
      // Set loading to true.
      setLoading(true);

      try {
        // Post the data to the server.
        const { data } = await axios({ method: 'post', url, data: formData });

        // Reset loading and null.
        setLoading(false);
        setError(null);

        // Call the success handler.
        handleSuccess(data);
      } catch (requestError) {
        const { response } = requestError || {};

        // Reset loading.
        setLoading(false);
        // Without a response or with a server error there may be no JSON body to read, e.g.
        // when a proxy answers instead, so fall back to a generic message.
        const fallback = !response || response.status >= 500 ? TEMPORARILY_UNAVAILABLE : null;
        setError(response?.data?.msg ?? response?.data?.errors?.[0]?.detail ?? fallback);

        if (typeof handleError === 'function') {
          handleError(requestError);
        }
      }
    },
    [url, handleSuccess, handleError, setLoading, setError],
  );

  return [onSubmit, loading, error];
};
